import { proxy } from 'valtio';
import { z } from 'zod';
import { getManyFromList } from '@soundxyz/utils';
import { createEnum, enumChecker } from '@soundxyz/utils/const';
import { LicenseType, MediaType, SongKeyType } from '@graphql/reactQuery';
import { PersistenceStorage } from '@store/persistence';
import { MAX_CHAR_LENGTH } from '../../validation';
import { ErrorMessages, initialReleaseInfoErrors, ReleaseInfoErrors } from './errors';

const numericInteger = z.union([z.string(), z.number()]).transform(v => {
  const stringNum = v.toString();
  return stringNum ? parseInt(stringNum, 10) : null;
});

export const UploadedMediaSchema = z.object({
  mediaType: z.nativeEnum(MediaType),
  uploadKey: z.string(),
});

export type UploadedMediaType = (typeof UploadedMediaSchema)['_input'];

const TrackUploadSchema = z.object({
  coverImage: UploadedMediaSchema.nullish().default(null),
  duration: z.number(),
  fileDetail: UploadedMediaSchema,
  normalizedPeaks: z.array(z.number()).optional().default([]),
  title: z.string(),
});

const TempTrackStepSchema = z.object({
  tempTitle: z.string().nullable().optional(),
  tempAudio: TrackUploadSchema.nullable().optional(),
  tempCoverImage: UploadedMediaSchema.nullable().optional(),
});

const TempTrackKeyOptions = TempTrackStepSchema.keyof().options;
const TempTrackKeys = createEnum(TempTrackKeyOptions);
export const isTempTrackKeys = enumChecker(TempTrackKeys);

export const ReleaseInfoValidatableFields = z
  .object({
    title: z
      .string()
      .nullable()
      .transform((value, ctx) => {
        if (value == null || value === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: ErrorMessages.title?.required_error,
          });
          return z.NEVER;
        }

        if (!value.match(/^.*[a-zA-Z0-9&$]+.*$/)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: ErrorMessages.title?.pattern_error,
          });
          return z.NEVER;
        }

        return value;
      }),
    tokenSymbol: z
      .string()
      .nullable()
      .transform((value, ctx) => {
        if (value == null || value === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: ErrorMessages.tokenSymbol.required_error,
          });
          return z.NEVER;
        }

        if (value.length > MAX_CHAR_LENGTH['tokenSymbol']) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Token Symbol must be fewer than 8 characters',
          });
          return z.NEVER;
        }

        if (!value.match(/^[A-Z0-9]+$/)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Token Symbol must be all capital letters or numbers',
          });
          return z.NEVER;
        }

        return value;
      }),
    tracks: z
      .array(TrackUploadSchema)
      .transform((value, ctx) => {
        if (value == null || value?.length === 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: ErrorMessages.tracks.required_error,
          });
          return z.NEVER;
        }

        return value;
      })
      .nullable()
      .refine(value => value !== null, {
        message: ErrorMessages.tracks.required_error,
      }),
    coverImage: UploadedMediaSchema.nullish().transform((value, ctx) => {
      if (!value || value == null) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: ErrorMessages.coverImage.required_error,
        });
        return z.NEVER;
      }
      return value;
    }),
    genre: z
      .string()
      .nullable()
      .transform((value, ctx) => {
        if (value == null || value === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: ErrorMessages.genre.required_error,
          });
          return z.NEVER;
        }
        return value;
      }),
    behindTheMusic: z
      .string()
      .nullable()
      .transform((value, ctx) => {
        if (value == null || value === '') {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: ErrorMessages.behindTheMusic.required_error,
          });
          return z.NEVER;
        }

        if (value.length > MAX_CHAR_LENGTH['behindTheMusic']) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Description must be fewer than ${MAX_CHAR_LENGTH['behindTheMusic']} characters`,
          });
          return z.NEVER;
        }
        return value;
      }),
    staticCoverImage: UploadedMediaSchema.nullable(),
    beatsPerMinute: numericInteger.nullish().default(null),
    key: z.nativeEnum(SongKeyType).nullish().default(null),
    lyrics: z
      .string()
      .nullish()
      .default(null)
      .transform((value, ctx) => {
        if (value && value.length > MAX_CHAR_LENGTH['lyrics']) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: `Lyrics must be fewer than ${MAX_CHAR_LENGTH['lyrics']} characters`,
          });
          return z.NEVER;
        }
        return value;
      }),
    license: z.nativeEnum(LicenseType).nullish().default(null),
    location: z.string().nullish().default(null),
    tempTrack: TempTrackStepSchema.nullable()
      .default({ tempTitle: null, tempCoverImage: null, tempAudio: null })
      .transform((value, ctx) => {
        if (!releaseInfoState.isTempTrack || !releaseInfoState.isAlbum) return value;

        if (releaseInfoState.isTempTrack && value == null) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: ErrorMessages.tempTrack?.missing_title,
            path: ['tempTitle'],
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: ErrorMessages.tempTrack?.missing_cover_image,
            path: ['tempCoverImage'],
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: ErrorMessages.tempTrack?.missing_audio,
            path: ['tempAudio'],
          });
          return;
        } else if (releaseInfoState.isTempTrack && value !== null) {
          if (!value.tempTitle || value.tempTitle === '') {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: ErrorMessages.tempTrack?.missing_title,
              path: ['tempTitle'],
            });
          }
          if (!value.tempCoverImage) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: ErrorMessages.tempTrack?.missing_cover_image,
              path: ['tempCoverImage'],
            });
          }
          if (!value.tempAudio) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: ErrorMessages.tempTrack?.missing_audio,
              path: ['tempAudio'],
            });
          }
          return;
        }
        return value;
      }),
    canCollectorsDownloadAudio: z
      .boolean()
      .refine(value => value !== null, {
        message: ErrorMessages.canCollectorsDownloadAudio.required_error,
      })
      .nullish(),
    curatorRewardsEnabled: z
      .boolean()
      .refine(value => value !== null, {
        message: ErrorMessages.curatorRewardsEnabled.required_error,
      })
      .nullish(),
    goldenEggImages: z
      .array(
        z
          .object({
            mediaType: z.nativeEnum(MediaType),
            uploadKey: z.string(),
          })
          .refine(value => value !== null, {
            message: ErrorMessages.goldenEggImages.required_error,
          })
          .nullish(),
      )
      .nullish(),
    rewards: z
      .array(
        z.object({
          title: z.string(),
          description: z.string().max(MAX_CHAR_LENGTH.rewardDescription),
        }),
      )
      .nullish(),
  })

  .superRefine((values, ctx) => {
    // validate golden eggs
    values.goldenEggImages?.forEach((goldenEgg, index) => {
      if (!goldenEgg) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: ErrorMessages.goldenEggImages.required_error,
          path: ['goldenEggImages', index],
        });
      }

      return;
    });

    // validate additional rewards
    values.rewards?.forEach((additionalReward, index) => {
      if (additionalReward.title.length < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: ErrorMessages.rewards.missing_reward_field_title,
          path: ['rewards', index, 'title'],
        });
      }
      if (additionalReward.title.length > MAX_CHAR_LENGTH.rewardTitle) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: ErrorMessages.rewards.reward_field_title_too_long,
          path: ['rewards', index, 'title'],
        });
      }
      if (additionalReward.description.length < 1) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: ErrorMessages.rewards.missing_reward_field_description,
          path: ['rewards', index, 'description'],
        });
      }
      if (additionalReward.description.length > MAX_CHAR_LENGTH.rewardDescription) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: ErrorMessages.rewards.reward_field_description_too_long,
          path: ['rewards', index, 'description'],
        });
      }

      return;
    });
  });

export const ReleaseInfoFields = ReleaseInfoValidatableFields;

export type ReleaseInfoFieldsKeys = keyof z.infer<typeof ReleaseInfoFields>;

export type ReleaseInfoFields = (typeof ReleaseInfoFields)['_input'];

const ReleaseInfoFieldOptions = ReleaseInfoValidatableFields._def['schema'].keyof().options;

export type ReleaseInfoFieldOptionsType = (typeof ReleaseInfoFieldOptions)[number];

const ReleaseInfoFieldOptionsType = createEnum(ReleaseInfoFieldOptions);

export type GoldenEggType = NonNullable<
  z.infer<typeof ReleaseInfoFields>['goldenEggImages']
>[number];

const ReleaseInfoUnnestedFieldOptions = createEnum(
  getManyFromList(ReleaseInfoFieldOptions, v =>
    v !== 'rewards' && v !== 'goldenEggImages' && v !== 'tempTrack' ? v : null,
  ),
);

export const isReleaseInfoUnnestedFieldKeysType = enumChecker(ReleaseInfoUnnestedFieldOptions);

export const initialReleaseInfoFields = () =>
  ({
    title: null,
    tokenSymbol: null,
    tracks: null,
    coverImage: null,
    canCollectorsDownloadAudio: true,
    curatorRewardsEnabled: true,
    goldenEggImages: [null],
    rewards: [],
    genre: null,
    behindTheMusic: null,
    staticCoverImage: null,
    beatsPerMinute: null,
    key: null,
    lyrics: null,
    license: null,
    location: null,
    tempTrack: { tempTitle: null, tempCoverImage: null, tempAudio: null },
  }) satisfies ReleaseInfoFields;

export const ReleaseInfoTouchedFields = z.object({
  title: z.boolean(),
  tokenSymbol: z.boolean(),
  tracks: z.boolean(),
  coverImage: z.boolean(),
  genre: z.boolean(),
  behindTheMusic: z.boolean(),
  staticCoverImage: z.boolean(),
  beatsPerMinute: z.boolean(),
  key: z.boolean(),
  lyrics: z.boolean(),
  license: z.boolean(),
  location: z.boolean(),
  tempTrack: z.object({
    tempTitle: z.boolean(),
    tempCoverImage: z.boolean(),
    tempAudio: z.boolean(),
  }),
  canCollectorsDownloadAudio: z.boolean(),
  curatorRewardsEnabled: z.boolean(),
  goldenEggImages: z.boolean().array(),
  rewards: z.object({ title: z.boolean(), description: z.boolean() }).array(),
});

const ReleaseInfoAccordionStateFields = z.object({
  value: z.string().optional(),
});

type ReleaseInfoTouchedFields = (typeof ReleaseInfoTouchedFields)['_input'];

export const initialReleaseInfoTouchedFields = () =>
  ({
    title: false,
    tokenSymbol: false,
    tracks: false,
    coverImage: false,
    genre: false,
    behindTheMusic: false,
    staticCoverImage: false,
    beatsPerMinute: false,
    key: false,
    lyrics: false,
    license: false,
    location: false,
    tempTrack: {
      tempTitle: false,
      tempCoverImage: false,
      tempAudio: false,
    },
    canCollectorsDownloadAudio: false,
    curatorRewardsEnabled: false,
    goldenEggImages: [],
    rewards: [],
  }) satisfies ReleaseInfoTouchedFields;

const initialReleaseInfoAccordionState = () => ({
  value: undefined,
});

const ReleaseInfoSchema = z.object({
  fields: ReleaseInfoFields,
  touched: ReleaseInfoTouchedFields,
  accordion: ReleaseInfoAccordionStateFields,
  errors: ReleaseInfoErrors,
  isValid: z.boolean(),
  isAlbum: z.boolean(),
  isTempTrack: z.boolean(),
});

export type ReleaseInfoState = (typeof ReleaseInfoSchema)['_input'];

export const releaseInfoState: ReleaseInfoState = proxy({
  errors: initialReleaseInfoErrors(),
  fields: initialReleaseInfoFields(),
  touched: initialReleaseInfoTouchedFields(),
  accordion: initialReleaseInfoAccordionState(),
  isValid: false,
  isAlbum: false,
  isTempTrack: false,
});

export const getEditedReleaseInfoPersistence = ({ releaseId }: { releaseId: string }) =>
  PersistenceStorage({
    schema: ReleaseInfoFields,
    key: `@soundxyz/editedReleaseInfo-${releaseId}`,
  });
